import type { DragEndEvent } from "@dnd-kit/core";
import type { ColumnDef, Row, SortingState } from "@tanstack/react-table";

import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowUp, ChevronsUpDown, GripVertical } from "lucide-react";
import React, { useMemo, useState } from "react";
import { Select } from "react-aria-components";

import { Pagination } from "@/components/pagination";
import {
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { validatePageSize } from "@/utils/paginationHelpers";

import { Loader } from "../Loader";

export interface PaginationMetadata {
  currentPage: number;
  itemsPerPage: number;
  totalItems: null | number; // Can be null when skipCount: true for performance optimization
  totalPages: null | number; // Can be null when total is null
}

export interface DraggableTableProps<TData> {
  className?: string;
  columns: ColumnDef<TData>[];
  data: TData[];
  dragHandleClassName?: string;
  enableDragAndDrop?: boolean;
  getRowClassName?: (row: Row<TData>) => string;
  getRowId?: (row: TData) => string;
  isLoading?: boolean;
  isServerSideSorting?: boolean; // indicate server-side sorting
  mobileColumnWidths?: Record<string, number>;
  onReorder?: (
    oldIndex: number,
    newIndex: number,
    reorderedData: TData[],
  ) => void;
  onSave?: (reorderedData: TData[]) => Promise<void>;
  onSort?: (sorting: SortingState) => void;
  pagination?: {
    metadata: PaginationMetadata;
    onItemsPerPageChange?: (itemsPerPage: number) => void;
    onPageChange: (page: number) => void;
    pageSizeOptions?: number[];
    showPageSizeSelector?: boolean;
  };
  sortableColumns?: string[];
  sorting?: SortingState;
}

interface DraggableRowProps<TData = unknown> {
  dragHandleClassName?: string;
  enableDragAndDrop?: boolean;
  getRowClassName?: (row: Row<TData>) => string;
  mobileColumnWidths?: Record<string, number>;
  row: Row<TData>;
}

function DraggableRow<TData = unknown>({
  dragHandleClassName,
  enableDragAndDrop = false,
  getRowClassName,
  isMobile,
  mobileColumnWidths,
  row,
}: { isMobile?: boolean } & DraggableRowProps<TData>) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    disabled: !enableDragAndDrop,
    id: row.id,
  });

  const style = enableDragAndDrop
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
      }
    : {};

  const extraClassName = getRowClassName ? getRowClassName(row) : "";

  return (
    <tr
      className={`group/row h-7xl bg-bg-primary outline-hidden ${
        enableDragAndDrop && isDragging ? "opacity-50 shadow-lg z-10" : ""
      } ${extraClassName}`}
      ref={enableDragAndDrop ? setNodeRef : undefined}
      style={style}
      {...(enableDragAndDrop ? attributes : {})}
    >
      {row.getVisibleCells().map((cell, index) => (
        <td
          className="border-b border-b-border-secondary px-3xl text-sm font-normal text-text-tertiary outline-hidden"
          key={cell.id}
          style={{
            maxWidth: isMobile
              ? (mobileColumnWidths?.[cell.column.id] ?? cell.column.getSize())
              : cell.column.getSize(),
            width: isMobile
              ? (mobileColumnWidths?.[cell.column.id] ?? cell.column.getSize())
              : cell.column.getSize(),
          }}
        >
          <div className="flex items-center gap-2">
            {enableDragAndDrop && index === 0 && (
              <button
                className={`flex items-center justify-center p-1 cursor-grab active:cursor-grabbing hover:bg-bg-secondary rounded transition-colors focus:outline-none focus:ring-2 focus:ring-border-brand focus:ring-offset-1 shrink-0 ${
                  dragHandleClassName || ""
                }`}
                type="button"
                {...listeners}
              >
                <GripVertical className="size-4 text-text-tertiary" />
              </button>
            )}
            <div
              className={`flex-1 min-w-0 ${isMobile ? "overflow-hidden" : ""}`}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </div>
          </div>
        </td>
      ))}
    </tr>
  );
}

export function DraggableTable<TData>({
  className,
  columns,
  data,
  dragHandleClassName,
  enableDragAndDrop = false,
  getRowClassName,
  getRowId,
  isLoading = false,
  isServerSideSorting = false,
  mobileColumnWidths,
  onReorder,
  onSave,
  onSort,
  pagination,
  sortableColumns = [],
  sorting,
}: DraggableTableProps<TData>) {
  const isMobile = useIsMobile();
  const showPageSizeSelector = Boolean(
    pagination?.showPageSizeSelector && pagination.onItemsPerPageChange,
  );
  const [localData, setLocalData] = useState<TData[]>(data);
  const [isSaving, setIsSaving] = useState(false);
  const [sortingState, setSortingState] = useState<SortingState>(sorting || []);

  React.useEffect(() => {
    setLocalData(data);
  }, [data]);

  React.useEffect(() => {
    if (sorting) {
      setSortingState(sorting);
    }
  }, [sorting]);

  const columnsWithDragHandle = useMemo(() => columns, [columns]);

  const mobileTotalWidth = useMemo(() => {
    if (!isMobile) return;

    let sum = 0;

    for (const col of columns) {
      const id = col.id as string;

      sum += mobileColumnWidths?.[id] ?? (col.size as number) ?? 150;
    }

    return sum;
  }, [isMobile, columns]);

  const table = useReactTable({
    columns: columnsWithDragHandle,
    data: localData,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getRowId: getRowId ? (row) => getRowId(row) : undefined,
    // Only enable client-side sorting when NOT using server-side sorting
    getSortedRowModel: isServerSideSorting ? undefined : getSortedRowModel(),
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === "function" ? updater(sortingState) : updater;

      setSortingState(newSorting);
      onSort?.(newSorting);
    },
    state: {
      sorting: sortingState,
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = localData.findIndex((item) => {
      const rowId = getRowId ? getRowId(item) : String(localData.indexOf(item));

      return rowId === active.id;
    });

    const newIndex = localData.findIndex((item) => {
      const rowId = getRowId ? getRowId(item) : String(localData.indexOf(item));

      return rowId === over.id;
    });

    if (oldIndex !== -1 && newIndex !== -1) {
      const reorderedData = arrayMove(localData, oldIndex, newIndex);

      setLocalData(reorderedData);
      onReorder?.(oldIndex, newIndex, reorderedData);
      if (onSave) {
        try {
          setIsSaving(true);
          await onSave(reorderedData);
        } catch (error) {
          console.error("Failed to save reordered data:", error);
          setLocalData(data);
        } finally {
          setIsSaving(false);
        }
      }
    }
  };

  const rowIds = useMemo(
    () =>
      enableDragAndDrop
        ? localData.map((item, index) =>
            getRowId ? getRowId(item) : String(index),
          )
        : [],
    [localData, getRowId, enableDragAndDrop],
  );

  if (isLoading) {
    return <Loader />;
  }

  const tableContent = (
    <table
      className={`${isMobile ? "w-auto" : "w-full"} outline-hidden`}
      style={{
        tableLayout: "fixed",
        ...(isMobile && mobileTotalWidth ? { width: mobileTotalWidth } : {}),
      }}
    >
      <thead className="sticky h-11 top-0 z-10 backdrop-blur-md outline-hidden">
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                className={`px-3xl text-xs font-semibold text-text-tertiary bg-bg-secondary outline-hidden text-left ${
                  sortableColumns.includes(header.column.id)
                    ? "cursor-pointer hover:bg-bg-tertiary"
                    : "cursor-default"
                }`}
                key={header.id}
                onClick={
                  sortableColumns.includes(header.column.id)
                    ? header.column.getToggleSortingHandler()
                    : undefined
                }
                style={{
                  width: isMobile
                    ? (mobileColumnWidths?.[header.id] ?? header.getSize())
                    : header.getSize(),
                }}
              >
                <div className="flex items-center gap-1">
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}

                  {sortableColumns.includes(header.column.id) && (
                    <span className="flex items-center justify-center outline-hidden relative">
                      <ArrowUp
                        aria-hidden
                        className={`absolute size-4 text-xs font-semibold text-text-tertiary transition-all duration-200 ${
                          header.column.getIsSorted()
                            ? "opacity-100 scale-100"
                            : "opacity-0 scale-75"
                        } ${
                          header.column.getIsSorted() === "desc"
                            ? "rotate-180"
                            : "rotate-0"
                        }`}
                      />
                      <ChevronsUpDown
                        className={`size-4 transition-all duration-200 ${
                          header.column.getIsSorted()
                            ? "opacity-0 scale-75"
                            : "opacity-100 scale-100"
                        }`}
                      />
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>

      <tbody>
        {enableDragAndDrop ? (
          <SortableContext
            items={rowIds}
            strategy={verticalListSortingStrategy}
          >
            {table.getRowModel().rows.map((row) => (
              <DraggableRow
                dragHandleClassName={dragHandleClassName}
                enableDragAndDrop={enableDragAndDrop}
                getRowClassName={getRowClassName}
                isMobile={isMobile}
                key={row.id}
                mobileColumnWidths={mobileColumnWidths}
                row={row}
              />
            ))}
          </SortableContext>
        ) : (
          table
            .getRowModel()
            .rows.map((row) => (
              <DraggableRow
                dragHandleClassName={dragHandleClassName}
                enableDragAndDrop={enableDragAndDrop}
                getRowClassName={getRowClassName}
                isMobile={isMobile}
                key={row.id}
                mobileColumnWidths={mobileColumnWidths}
                row={row}
              />
            ))
        )}
      </tbody>
    </table>
  );

  return (
    <div className={`relative ${className || ""}`}>
      <div className="overflow-auto scrollbar-none">
        {isSaving && (
          <div className="absolute inset-0 bg-bg-primary/50 flex items-center justify-center z-20" />
        )}

        {enableDragAndDrop ? (
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            sensors={sensors}
          >
            {tableContent}
          </DndContext>
        ) : (
          tableContent
        )}

        {localData.length === 0 && (
          <div className="flex items-center justify-center p-8">
            <div className="text-text-tertiary">No data available</div>
          </div>
        )}
      </div>

      {pagination && (
        <div
          className={`@container flex w-full items-center gap-md rounded-b-xl bg-bg-primary px-2xl py-md ${showPageSizeSelector || !isMobile ? "justify-between" : "justify-center"} ${localData.length === 0 ? "border-t border-border-secondary" : ""}`}
        >
          {showPageSizeSelector &&
            (() => {
              const pageSizeOptions = pagination.pageSizeOptions || [
                10, 25, 50, 100,
              ];
              const { validPageSize } = validatePageSize(
                pagination.metadata.itemsPerPage,
                pagination.metadata.totalItems ?? Number.MAX_SAFE_INTEGER,
              );

              const safeSelectedKey = pageSizeOptions.includes(validPageSize)
                ? validPageSize
                : pageSizeOptions[0] || 10;

              return (
                <Select
                  aria-label="select items per page"
                  className="w-auto min-w-[159px] shrink-0 border-none bg-bg-primary pb-4 pt-3"
                  onSelectionChange={(selectedKey: null | number | string) => {
                    if (selectedKey === null) return;
                    pagination.onItemsPerPageChange?.(Number(selectedKey));
                    pagination.onPageChange(1);
                  }}
                  selectedKey={safeSelectedKey}
                >
                  <SelectButton className="cursor-pointer text-sm text-button-secondary-fg font-semibold">
                    Show:&nbsp;
                  </SelectButton>
                  <SelectPopover>
                    <SelectListBox>
                      {pageSizeOptions.map((size) => (
                        <SelectListItem
                          className="text-button-secondary-fg"
                          id={size}
                          key={size}
                        >
                          {size} items
                        </SelectListItem>
                      ))}
                    </SelectListBox>
                  </SelectPopover>
                </Select>
              );
            })()}

          <div className="flex shrink-0 items-center gap-4">
            {!isMobile && pagination.metadata.totalItems !== null && (
              <span className="shrink-0 whitespace-nowrap text-sm text-text-tertiary">
                {(() => {
                  const { currentPage, itemsPerPage, totalItems } =
                    pagination.metadata;

                  if (totalItems === 0) {
                    return "0 of 0";
                  }

                  const firstItem = (currentPage - 1) * itemsPerPage + 1;
                  const lastItem = Math.min(
                    currentPage * itemsPerPage,
                    totalItems!,
                  );

                  return `${firstItem} - ${lastItem} of ${totalItems}`;
                })()}
              </span>
            )}

            {pagination.metadata.totalPages !== null && (
              <Pagination
                currentPage={pagination.metadata.currentPage}
                onPageChange={pagination.onPageChange}
                totalPages={pagination.metadata.totalPages}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
